/**
 * Registration field rules and the refusal-to-field mapping.
 *
 * Kept out of the route component so both can be exercised without mounting
 * React: the username rule is per-country data, and the mapping from a server
 * refusal to the input it belongs under is the whole of the "which field is
 * duplicated?" fix.
 */
import { ApiError } from '../../lib/api/error.js';
import { DEFAULT_COUNTRY_ISO, findCountryByIso } from '../../lib/country-codes.js';

/** Digits, and nothing else — the username IS a phone number. */
const DIGITS_ONLY = /^\d+$/;

/**
 * How many digits the country expects for a local number, or null when the
 * count is not settled for that market and the generic E.164 shape check has to
 * stand in.
 */
export function localDigitsFor(iso: string): number | null {
  const country = findCountryByIso(iso) ?? findCountryByIso(DEFAULT_COUNTRY_ISO);
  return country?.localDigits ?? null;
}

export interface FieldError {
  key: string;
  values?: Record<string, string | number>;
}

/**
 * The username rule: a local phone number, digits only, of the country's
 * length. Returns undefined when the value is acceptable.
 *
 * `isE164` is the generic fallback the caller supplies for a country whose
 * digit count is not pinned down — it keeps a market without a `localDigits`
 * entry usable instead of rejecting every number there.
 */
export function validateUsername(
  value: string,
  iso: string,
  isE164: (local: string) => boolean,
): FieldError | undefined {
  if (!value) return { key: 'register.errors.usernameRequired' };
  if (!DIGITS_ONLY.test(value)) return { key: 'register.errors.usernameDigitsOnly' };

  const expected = localDigitsFor(iso);
  if (expected !== null) {
    if (value.length !== expected) {
      return { key: 'register.errors.usernameDigits', values: { digits: expected } };
    }
    return undefined;
  }

  return isE164(value) ? undefined : { key: 'register.errors.phoneInvalid' };
}

/**
 * A player's bank account number wherever one is typed (register, forgot
 * password, add bank): digits only, 8-20 of them. Lao numbers run past 14
 * (BCEL's are 18); svc-member itself accepts 3-32.
 */
export const BANK_ACCOUNT_NO_MAX = 20;
export const BANK_ACCOUNT_NO_RE = new RegExp(`^\\d{8,${BANK_ACCOUNT_NO_MAX}}$`);

export function validateBankAccountNo(value: string): FieldError | undefined {
  if (!value) return { key: 'register.errors.bankAccountRequired' };
  if (!BANK_ACCOUNT_NO_RE.test(value)) return { key: 'register.errors.bankAccountInvalid' };
  return undefined;
}

export function validateNamePart(value: string, key: string): FieldError | undefined {
  if (!value.trim()) return { key };
  if (value.trim().length > 64) return { key: 'register.errors.nameTooLong' };
  return undefined;
}

export type DuplicateField = 'username' | 'phone' | 'bankAccount';

const FIELD_BY_CODE: Record<string, DuplicateField> = {
  'MEMBER-001': 'username',
  'MEMBER-201': 'phone',
  'MEMBER-101': 'bankAccount',
};

const ALL_FIELDS: DuplicateField[] = ['username', 'phone', 'bankAccount'];

/**
 * Which values the server says are already in use.
 *
 * `extensions.duplicateFields` is the whole set; the code alone is the fallback
 * for a service that predates it, and names exactly one.
 */
export function duplicateFieldsFromError(err: unknown): DuplicateField[] {
  if (!(err instanceof ApiError)) return [];
  const listed = err.extensions?.duplicateFields;
  if (Array.isArray(listed)) {
    const named = listed.filter((f): f is DuplicateField =>
      ALL_FIELDS.includes(f as DuplicateField),
    );
    if (named.length > 0) return ALL_FIELDS.filter((f) => named.includes(f));
  }
  // Own-property check before the index: `err.code` comes straight off the
  // wire, and `constructor` / `toString` would otherwise read a prototype
  // member back as if it were one of the three field names.
  const single =
    err.code && Object.hasOwn(FIELD_BY_CODE, err.code) ? FIELD_BY_CODE[err.code] : undefined;
  return single ? [single] : [];
}

/** The form inputs a server refusal can be attached to. */
export type ServerFieldErrors = Partial<
  Record<'username' | 'password' | 'bankAccountNo', string[]>
>;

/**
 * Turn a refusal into per-input messages, or return nothing so the caller falls
 * back to a toast.
 *
 * The phone duplicate lands under the username box because the two are the same
 * value now — and is dropped when the username is duplicated too, which it
 * always is for a returning player: one box cannot usefully say the number is
 * taken twice.
 */
export function serverFieldErrors(
  err: unknown,
  translate: (code: string) => string,
): ServerFieldErrors {
  const errors: ServerFieldErrors = {};

  if (err instanceof ApiError && err.code === 'AUTH-PW-WEAK') {
    errors.password = [translate('AUTH-PW-WEAK')];
    return errors;
  }

  const fields = duplicateFieldsFromError(err);
  const username: string[] = [];
  if (fields.includes('username')) username.push(translate('MEMBER-001'));
  else if (fields.includes('phone')) username.push(translate('MEMBER-201'));
  if (username.length > 0) errors.username = username;
  if (fields.includes('bankAccount')) errors.bankAccountNo = [translate('MEMBER-101')];

  return errors;
}

/** True when the refusal was fully accounted for by the inline field errors. */
export function hasFieldErrors(errors: ServerFieldErrors): boolean {
  return Object.values(errors).some((messages) => (messages?.length ?? 0) > 0);
}
